import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Mic2, 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  PlayCircle, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  FileText,
  Calendar,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { getProjectBySlug, listEpisodes, deleteEpisode, reingestEpisode } from '../../lib/apiClient';
import ImportEpisodeModal from '../../archive/Components/episodes/ImportEpisodeModal';

export default function EpisodesPage() {
  const { brandSlug } = useParams(); // Assuming param name is brandSlug from App.js
  // Actually App.js says: path="brand/:brandSlug/episodes"
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [project, setProject] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [menuEpisodeId, setMenuEpisodeId] = useState(null);
  const [actionEpisode, setActionEpisode] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reingestingEpisodeId, setReingestingEpisodeId] = useState(null);

  const loadEpisodesForBrand = async (slug) => {
    try {
      setLoading(true);
      const projRes = await getProjectBySlug(slug);
      if (!projRes?.data) throw new Error('Project not found');
      setProject(projRes.data);

      const epRes = await listEpisodes({ projectId: projRes.data.id });
      // API returns { episodes: [], pagination: {} }
      setEpisodes(epRes?.episodes || []);
    } catch (e) {
      console.error('Failed to load episodes', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
      if (brandSlug) {
        loadEpisodesForBrand(brandSlug);
      }
  }, [brandSlug]);

  const filteredEpisodes = episodes.filter(ep => {
    // Map backend status to UI status
    const status = getStatus(ep);
    const matchesSearch = ep.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'All' || status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) return <div className="h-screen flex items-center justify-center bg-background text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const handleEpisodeActions = (ep) => {
    // Toggle menu for this row
    setMenuEpisodeId(prev => (prev === ep.episodeId ? null : ep.episodeId));
    setActionEpisode(ep);
  };

  const buildWorkspaceUrl = (episodeId) => {
    const projectId = project?.id;
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    if (brandSlug) params.set('brandSlug', brandSlug);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return `/workspace/episode/${episodeId}${suffix}`;
  };

  const handleOpenWorkspace = (ep) => {
    // Include projectId and brandSlug in the workspace URL so backend auth and navigation can resolve correctly
    const url = buildWorkspaceUrl(ep.episodeId);
    navigate(url, { state: { episodeTitle: ep.title } });
    setMenuEpisodeId(null);
    setActionEpisode(null);
  };

  const handleConfirmDelete = async () => {
    if (!actionEpisode || !project) return;
    await handleEpisodeDelete(actionEpisode, project, brandSlug, loadEpisodesForBrand, setImportMessage);
    setIsDeleteModalOpen(false);
    setMenuEpisodeId(null);
    setActionEpisode(null);
  };

  const handleReingest = async (ep) => {
    if (!ep || !project) return;
    // Prevent double-clicks
    if (reingestingEpisodeId) return;
    
    const targetEpisodeId = ep.episodeId;
    console.log('[EpisodesPage] Re-ingesting episode:', targetEpisodeId, ep.title);
    
    try {
      setReingestingEpisodeId(targetEpisodeId);
      setMenuEpisodeId(null);
      setActionEpisode(null);
      
      const result = await reingestEpisode(targetEpisodeId, { projectId: project.id });
      console.log('[EpisodesPage] Re-ingest result:', result);
      
      setImportMessage(result.message || 'Re-ingestion started. This may take a few minutes.');
      setTimeout(() => setImportMessage(''), 8000);
      // Refresh episodes list to show updated status
      if (brandSlug) {
        loadEpisodesForBrand(brandSlug);
      }
    } catch (err) {
      console.error('Failed to re-ingest episode', targetEpisodeId, err);
      const errorMsg = err.message || 'Failed to start re-ingestion';
      setImportMessage(`Error: ${errorMsg}`);
      setTimeout(() => setImportMessage(''), 6000);
    } finally {
      setReingestingEpisodeId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      
      {/* Header */}
      <header className="bg-surface/50 border-b border-white/10 sticky top-0 z-10 backdrop-blur-md">
        <div className="container mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <Link to={`/app/brand/${brandSlug}/dashboard`} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
               <ArrowRight className="w-5 h-5 text-slate-400 rotate-180" />
             </Link>
             <div>
               <h1 className="font-bold text-lg text-white">Episodes</h1>
               <p className="text-xs text-slate-400">Manage your content library.</p>
             </div>
          </div>
          <button
            onClick={() => setIsImportOpen(true)}
            className="px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary-hover transition-colors flex items-center gap-2 shadow-glow-blue"
          >
            <Plus className="w-4 h-4" />
            Upload Episode
          </button>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-6 py-8 space-y-6">

        {importMessage && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs px-3 py-2 rounded-lg mb-2">
            {importMessage}
          </div>
        )}

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-surface border border-white/10 rounded-lg p-1">
             {['All', 'Published', 'Draft', 'Processing', 'Failed'].map((status) => (
               <button
                 key={status}
                 onClick={() => setFilterStatus(status)}
                 className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                   filterStatus === status 
                     ? 'bg-white/10 text-white' 
                     : 'text-slate-400 hover:text-white hover:bg-white/5'
                 }`}
               >
                 {status}
               </button>
             ))}
          </div>

          {/* Search */}
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input 
              type="text" 
              placeholder="Search episodes..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-surface border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-300 focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>

        {/* Episodes List */}
        <div className="bg-surface border border-white/5 rounded-2xl overflow-hidden">
           <div className="overflow-x-auto">
             <table className="w-full text-left text-sm">
               <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider font-medium border-b border-white/5">
                 <tr>
                   <th className="px-6 py-4 w-1/2">Episode</th>
                   <th className="px-6 py-4">Status</th>
                   <th className="px-6 py-4">Insights</th>
                   <th className="px-6 py-4">Date</th>
                   <th className="px-6 py-4 text-right">Actions</th>
                 </tr>
               </thead>
               <tbody className="divide-y divide-white/5">
                 {filteredEpisodes.map((ep) => (
                   <tr key={ep.episodeId} className="group hover:bg-white/5 transition-colors">
                     <td className="px-6 py-4">
                       <div className="flex items-center gap-4">
                         <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                           <Mic2 className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                         </div>
                         <div>
                           <Link
                             to={buildWorkspaceUrl(ep.episodeId)}
                             state={{ episodeTitle: ep.title }}
                             className="font-medium text-slate-200 group-hover:text-primary transition-colors block mb-0.5"
                           >
                             {ep.title}
                           </Link>
                           <div className="flex items-center gap-2 text-xs text-slate-500">
                             <span className="flex items-center gap-1"><PlayCircle className="w-3 h-3" /> {formatDuration(ep.durationSec)}</span>
                           </div>
                         </div>
                       </div>
                     </td>
                     <td className="px-6 py-4">
                       <StatusBadge status={getStatus(ep)} />
                     </td>
                     <td className="px-6 py-4">
                       <div className="flex items-center gap-1.5 text-slate-300">
                         <FileText className="w-4 h-4 text-slate-500" />
                         <span>{ep.insightCount || 0}</span>
                       </div>
                     </td>
                     <td className="px-6 py-4">
                       <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                         <Calendar className="w-3 h-3" />
                         <span>{new Date(ep.createdAt).toLocaleDateString()}</span>
                       </div>
                     </td>
                     <td className="px-6 py-4 text-right relative">
                       <button
                         className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors"
                         onClick={() => handleEpisodeActions(ep)}
                       >
                         <MoreHorizontal className="w-4 h-4" />
                       </button>
                        {menuEpisodeId === ep.episodeId && (
                          <div className="absolute right-4 mt-2 w-44 rounded-lg border border-white/10 bg-surface shadow-lg z-10 text-xs">
                            <button
                              className="w-full px-3 py-2 text-left text-slate-200 hover:bg-white/5"
                              onClick={() => handleOpenWorkspace(ep)}
                            >
                              Open Workspace
                            </button>
                            <button
                              className="w-full px-3 py-2 text-left text-slate-200 hover:bg-white/5 border-t border-white/5"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReingest(ep);
                              }}
                            >
                              Re-ingest Episode
                            </button>
                            <button
                              className="w-full px-3 py-2 text-left text-red-400 hover:bg-red-500/10 border-t border-white/5"
                              onClick={() => {
                                setIsDeleteModalOpen(true);
                                setActionEpisode(ep);
                              }}
                            >
                              Delete Episode
                            </button>
                          </div>
                        )}
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
           
           {filteredEpisodes.length === 0 && (
             <div className="p-12 text-center">
               <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                 <Search className="w-6 h-6 text-slate-500" />
               </div>
               <h3 className="text-white font-medium mb-1">No episodes found</h3>
               <p className="text-slate-400 text-sm">Try adjusting your search or filters.</p>
             </div>
           )}
        </div>

      </main>

      {/* Episode Import Modal */}
      {project && (
        <ImportEpisodeModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onConfirm={() => {
            setIsImportOpen(false);
            setImportMessage('Episode import started. Processing may take a few minutes.');
            if (brandSlug) {
              // Refresh episodes list in the background
              loadEpisodesForBrand(brandSlug);
            }
            // Auto-hide message after a short delay
            setTimeout(() => setImportMessage(''), 5000);
          }}
          projectId={project.id}
        />
      )}

      {/* Delete Episode Modal */}
      {isDeleteModalOpen && actionEpisode && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-surface p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-white mb-2">Delete this episode?</h2>
            <p className="text-sm text-slate-300 mb-4">
              This will remove <span className="font-semibold">{actionEpisode.title}</span> and its associated
              insights and campaign data for this brand. This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3 mt-4">
              <button
                className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-slate-200 hover:bg-white/10"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setMenuEpisodeId(null);
                  setActionEpisode(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-xs rounded-lg bg-red-500 text-white hover:bg-red-600"
                onClick={handleConfirmDelete}
              >
                Delete Episode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

async function handleEpisodeDelete(ep, project, brandSlug, loadEpisodesForBrand, setImportMessage) {
  if (!project) return;
  try {
    await deleteEpisode(ep.episodeId, { projectId: project.id });
    // Reload episodes
    if (brandSlug) {
      await loadEpisodesForBrand(brandSlug);
    }
    if (setImportMessage) {
      setImportMessage('Episode deleted.');
      setTimeout(() => setImportMessage(''), 4000);
    }
  } catch (err) {
    console.error('Failed to delete episode', err);
    alert('Failed to delete episode. Please try again.');
  }
}

function getStatus(episode) {
    if (!episode.job) return 'Draft'; // No job = just created? Or assume draft.
    const stage = episode.job.stage;
    if (stage === 'ready') return episode.insightCount > 0 ? 'Published' : 'Draft'; // Ready but no insights?
    // Or mapped:
    if (stage === 'error' || stage === 'failed') return 'Failed';
    if (['queued', 'transcribing', 'cleaning', 'enriching'].includes(stage)) return 'Processing';
    return 'Draft';
}

function formatDuration(sec) {
    if (!sec) return '--:--';
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

function StatusBadge({ status }) {
  const styles = {
    'Published': 'bg-green-500/10 text-green-400 border-green-500/20',
    'Draft': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    'Processing': 'bg-blue-500/10 text-blue-400 border-blue-500/20 animate-pulse',
    'Failed': 'bg-red-500/10 text-red-400 border-red-500/20'
  };

  const icons = {
    'Published': <CheckCircle2 className="w-3 h-3" />,
    'Draft': <Clock className="w-3 h-3" />,
    'Processing': <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />,
    'Failed': <AlertCircle className="w-3 h-3" />
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['Draft']}`}>
      {icons[status]}
      {status}
    </span>
  );
}
